import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useContentConfig } from '../hooks';

interface ChatFooterProps {
  onSend: (message: string) => void;
  onAttach?: () => void;
}

const ChatInput = styled('textarea', {
  width: '100%',
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.regular',
  lineHeight: '1.4',
  minHeight: 20,
  maxHeight: 40,
  resize: 'none',
  overflowY: 'auto',
  fontFamily: 'inherit',
  paddingRight: '$core.spacing.100',
  border: 0,
  outline: 'none !important',
  '&::placeholder': {
    color: '$core.color.neutral.50',
  },
});

const ChatFooter: React.FC<ChatFooterProps> = ({ onSend }) => {
  const { data: config } = useContentConfig();
  const placeholder = config?.content?.footer?.inputPlaceholder;
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.style.height = 'auto';
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 40)}px`;
  }, [message]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (trimmed.length > 0) {
      onSend(trimmed);
      setMessage('');
    }
  };

  const hasText = message.trim().length > 0;

  return (
    <FooterWrapper>
      <Container>
        <ChatInput
          ref={inputRef}
          value={message}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setMessage(event.currentTarget.value)
          }
          placeholder={placeholder ?? 'Type your message...'}
          rows={1}
          onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
          aria-label={placeholder ?? 'Type your message...'}
        />
      </Container>
      <SendButton
        active={hasText}
        onClick={handleSend}
        type="button"
        aria-label="Send message"
        disabled={!hasText}
      >
        <IconSymbol icon="send" />
      </SendButton>
    </FooterWrapper>
  );
};
const FooterWrapper = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '$core.spacing.100',
});

const Container = styled('div', {
  width: '100%',

  '& textarea': {
    width: '100%',
    padding: '8px 7px 5px 12px',
    border: '1px solid $core.color.neutral.20',
    borderRadius: '10px',
    minHeight: 40,
  },
});

const SendButton = styled('button', {
  width: 40,
  minWidth: 40,
  height: 40,
  minHeight: 40,
  borderRadius: '50%',
  marginRight: 4,
  border: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$core.color.brand.60',

  '& span.abyss-icon-symbol': {
    color: '$core.color.neutral.0',
  },
});

export default ChatFooter;
